// Data service utility functions for handling Firestore and file operations

import { getFirestore, collection, doc, getDoc, setDoc, addDoc, updateDoc, deleteDoc, getDocs } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { app } from './firebase';

// Initialize Firestore
const db = getFirestore(app);
const storage = getStorage(app);

// Hardcoded data for the application
const hardcodedData = {
  collections: {
    exhibits: [
      { id: '1', title: 'Exhibit A', description: 'Description of Exhibit A' },
      { id: '2', title: 'Exhibit B', description: 'Description of Exhibit B' }
    ],
    timeline: [
      { id: '1', event: 'Event 1', date: '2025-01-01' },
      { id: '2', event: 'Event 2', date: '2025-02-01' }
    ]
  }
};

/**
 * Fetch data from a collection (hardcoded)
 * @param {string} collectionName - The name of the collection to fetch from
 * @returns {Array} - The array of documents
 */
export const getCollection = async (collectionName) => {
  return hardcodedData.collections[collectionName] || [];
};

/**
 * Get a document by ID (hardcoded)
 * @param {string} collectionName - The name of the collection
 * @param {string} documentId - The ID of the document
 * @returns {Object|null} - The document data or null if not found
 */
export const getDocument = async (collectionName, documentId) => {
  const collection = hardcodedData.collections[collectionName] || [];
  return collection.find((doc) => doc.id === documentId) || null;
};

/**
 * Add a new document to a collection (not supported in hardcoded mode)
 */
export const addDocument = async () => {
  throw new Error('addDocument is not supported in hardcoded mode.');
};

/**
 * Update an existing document (not supported in hardcoded mode)
 */
export const updateDocument = async () => {
  throw new Error('updateDocument is not supported in hardcoded mode.');
};

/**
 * Delete a document (not supported in hardcoded mode)
 */
export const deleteDocument = async () => {
  throw new Error('deleteDocument is not supported in hardcoded mode.');
};

/**
 * Upload a file to Storage
 * @param {File} file - The file to upload
 * @param {string} path - The path to save the file in Storage
 * @returns {Object} - The file metadata including URL
 */
export const uploadFile = async (file, path) => {
  try {
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, file);
    const url = await getDownloadURL(snapshot.ref);
    
    return {
      path,
      url,
      name: file.name,
      contentType: file.type,
      size: file.size
    };
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

/**
 * Delete a file from Storage
 * @param {string} path - The path of the file to delete
 */
export const deleteFile = async (path) => {
  try {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
    return true;
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

/**
 * Migrate data from JSON files to Firestore
 * @param {string} collectionName - The Firestore collection name
 * @param {Array} jsonData - The JSON data to migrate
 */
export const migrateDataFromJSONFiles = async (collectionName, jsonData) => {
  try {
    const batch = db.batch();
    jsonData.forEach((data) => {
      const docRef = doc(collection(db, collectionName));
      batch.set(docRef, data);
    });
    await batch.commit();
    console.log('Data migration completed successfully.');
  } catch (error) {
    console.error('Error migrating data from JSON files:', error);
    throw error;
  }
};

/**
 * Export Firestore data to JSON
 * @param {string} collectionName - The Firestore collection name
 * @returns {Array} - The exported JSON data
 */
export const exportDataToJSON = async (collectionName) => {
  try {
    const querySnapshot = await getDocs(collection(db, collectionName));
    const jsonData = [];
    querySnapshot.forEach((doc) => {
      jsonData.push({ id: doc.id, ...doc.data() });
    });
    return jsonData;
  } catch (error) {
    console.error('Error exporting data to JSON:', error);
    throw error;
  }
};